import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { launchCelebration } from "../utils/confetti"

// Warm sign-up (#3018): an office that joined from another office's referral
// has been given 30 days of Pro by that office. Two elements carry this
// controller.
//
// The banner (shared/_gifted_trial_banner) is a link to Home. Clicking it
// records the dismissal so the banner does not render again, and lets the
// link navigate.
//
// The modal (shared/_gifted_trial_modal) opens itself when Home connects.
// It renders with data-gifted-trial-open, which the demo-tour controller reads
// as "wait": the tray holds back until this modal has closed, then docks. The
// attribute is in the markup rather than set here so the wait does not depend
// on which controller connects first.
export default class extends Controller {
  static targets = ["primary"]
  static values = { endpoint: String }

  connect() {
    if (!this.element.classList.contains("modal")) return
    // Turbo's restoration preview must not open it a second time.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.modal = Modal.getOrCreateInstance(this.element)

    // Keep the open modal and its backdrop out of Turbo's page snapshot, so
    // Back never brings a ghost of it up.
    this.scrub = () => {
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
      document.body.classList.remove("modal-open")
      this.element.remove()
    }
    document.addEventListener("turbo:before-cache", this.scrub, { once: true })

    // Bootstrap focuses the dialog itself on open. The one action in it is
    // what the user came for, so focus lands there and Enter takes it.
    this.focusPrimary = () => this.primaryTarget.focus()
    // Confetti across the whole screen as the gift is announced: the same
    // celebration the tutorial's completion modal plays. Its burst layer sits
    // between Bootstrap's backdrop (1050) and the modal (1055), so the burst
    // reads as coming from behind the card.
    //
    // The card is not laid out yet on show.bs.modal, so the burst takes the
    // celebration's own origin, the middle of the screen, where the centered
    // card lands.
    this.celebrate = () => launchCelebration(null, { behindZ: 1052, frontZ: 2000 })
    this.element.addEventListener("show.bs.modal", this.celebrate, { once: true })
    this.markClosing = () => this.closing()
    this.element.addEventListener("shown.bs.modal", this.focusPrimary)
    this.element.addEventListener("hide.bs.modal", this.markClosing)

    this.modal.show()
  }

  disconnect() {
    if (!this.modal) return

    document.removeEventListener("turbo:before-cache", this.scrub)
    this.element.removeEventListener("shown.bs.modal", this.focusPrimary)
    this.element.removeEventListener("show.bs.modal", this.celebrate)
    this.element.removeEventListener("hide.bs.modal", this.markClosing)
  }

  // Banner hover: the light spot follows the pointer. Position only, as two
  // custom properties; the stylesheet draws the spot and fades it in.
  track(event) {
    const box = this.element.getBoundingClientRect()
    this.element.style.setProperty("--spot-x", `${event.clientX - box.left}px`)
    this.element.style.setProperty("--spot-y", `${event.clientY - box.top}px`)
  }

  // Banner click. The request is fire-and-forget: the visit to Home must not
  // wait on it, and if it fails the banner simply shows once more.
  takeTour() {
    this.record()
  }

  // Every way out of the modal — X, the button, Esc, the backdrop — lands
  // here, so they all end in the same place: the tray docking.
  closing() {
    delete this.element.dataset.giftedTrialOpen
    // Seeing the modal retires the banner server-side; any banner already on
    // this page goes with it rather than waiting for the next navigation.
    document.querySelectorAll(".gifted-trial-banner").forEach((el) => el.remove())
    this.record()
  }

  record() {
    if (!this.hasEndpointValue) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.endpointValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      keepalive: true,
    }).catch(() => {})
  }
}
